import React, { useEffect, useState, useCallback, useRef } from "react";
import { Moon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";

interface MediaRestModeProps {
  isOpen: boolean;
  onWake: () => void;
}

/**
 * Media Rest Mode — replaces the controls area while media keeps playing.
 * Fits into the same vertical space as the control buttons + info bar.
 * Wake mechanism: 3 blinks (select actions) via scanningAction or keyboard.
 */
export function MediaRestMode({ isOpen, onWake }: MediaRestModeProps) {
  const { t } = useTranslation();
  const [selectCount, setSelectCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset on open & steal focus from iframe
  useEffect(() => {
    if (isOpen) {
      setSelectCount(0);
      const raf = requestAnimationFrame(() => {
        containerRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isOpen]);

  const handleWake = useCallback(() => {
    onWake();
  }, [onWake]);

  const incrementSelect = useCallback(() => {
    setSelectCount((prev) => {
      const next = prev + 1;
      if (next >= 3) {
        setTimeout(() => handleWake(), 0);
      }
      return next;
    });
  }, [handleWake]);

  // Listen for select actions (blinks / external switch)
  useEffect(() => {
    if (!isOpen) return;
    const handleScanAction = (e: CustomEvent<{ action: string }>) => {
      if (e.detail.action === "select") incrementSelect();
    };
    window.addEventListener("scanningAction", handleScanAction as EventListener);
    return () => window.removeEventListener("scanningAction", handleScanAction as EventListener);
  }, [isOpen, incrementSelect]);

  // Keyboard handler on the focused container
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); handleWake(); return; }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); incrementSelect(); }
  }, [handleWake, incrementSelect]);

  // Global keydown fallback
  useEffect(() => {
    if (!isOpen) return;
    const globalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); handleWake(); return; }
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); incrementSelect(); }
    };
    window.addEventListener("keydown", globalKeyDown, true);
    return () => window.removeEventListener("keydown", globalKeyDown, true);
  }, [isOpen, handleWake, incrementSelect]);

  // Re-steal focus from iframe periodically
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      if (document.activeElement?.tagName === "IFRAME") containerRef.current?.focus();
    }, 500);
    return () => clearInterval(interval);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-center gap-3 py-3 px-4 outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Compact rest mode indicator — single row with icon + text + dots */}
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Moon className="w-8 h-8 text-primary" />
            </motion.div>

            <div className="flex flex-col items-start">
              <h2 className="text-xl font-bold text-foreground leading-tight">{t("restMode")}</h2>
              <p className="text-sm text-muted-foreground leading-tight">
                {t("mediaRestBlinkInstruction")}
              </p>
            </div>

            {/* Progress dots */}
            <div className="flex gap-2">
              {[1, 2, 3].map((num) => (
                <div
                  key={num}
                  className={cn(
                    "w-8 h-8 rounded-full border-3 flex items-center justify-center transition-all duration-300",
                    selectCount >= num
                      ? "bg-primary border-primary text-primary-foreground scale-110"
                      : "border-muted-foreground/30 text-muted-foreground/30"
                  )}
                >
                  <span className="text-sm font-bold">{num}</span>
                </div>
              ))}
            </div>
          </div>

        </motion.div>
      )}
    </AnimatePresence>
  );
}
